import { useClickOutside } from '@cma/common'
import { Select } from '@helix/select'
import { useRef, useState } from 'react'
import { OptionProps } from 'react-select'
import { OrganizationSelectProps, OrgOption } from './types'
import { isClickOutsideException } from './utils'
import { useOrganizationOptions } from './hooks/useOrganizationOptions'
import { useOrganizationSelection } from './hooks/useOrganizationSelection'
import { OrganizationList } from './OrganizationList'
import { OrganizationHierarchyView } from './OrganizationHierarchyView'

export function OrgSelect({
  selectedTarget,
  filterSelected,
  onChange,
  onModalCloseClick
}: OrganizationSelectProps) {
  const selectRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [term, setTerm] = useState('')
  const [showMenu, setShowMenu] = useState(false)
  const [showOrgView, setShowOrgView] = useState(false)

  const { options: hierarchyOptions } = useOrganizationOptions(
    filterSelected,
    selectedTarget,
    'hierarchy'
  )
  const { options: listOptions } = useOrganizationOptions(
    filterSelected,
    selectedTarget,
    'list'
  )
  const { selectedOptions, handleSelectionChange, handleOptionToggle } =
    useOrganizationSelection(onChange)

  useClickOutside(
    showOrgView || showMenu,
    containerRef,
    selectRef,
    (clickedElement) => {
      if (isClickOutsideException(clickedElement || null)) {
        if (
          clickedElement?.closest('button')?.querySelector('.sr-only')
            ?.textContent === 'Close Modal'
        ) {
          setShowOrgView(false)
          setShowMenu(false)
          onModalCloseClick?.()
          return
        }
        return
      }

      setShowOrgView(false)
      setShowMenu(false)
      setTerm(term)
    }
  )

  const resetSearch = () => {
    if (term) {
      setTerm('')
    }
  }

  const filteredListOptions = listOptions.filter((option) =>
    option.label.toLowerCase().includes(term.toLowerCase())
  )

  return (
    <div className="relative" ref={selectRef}>
      <Select
        isMulti
        aria-label="select location"
        getOptionValue={(o: OrgOption) => o.value.id}
        getOptionLabel={(o: OrgOption) => o.label}
        options={listOptions}
        value={selectedOptions}
        inputValue={term}
        onInputChange={(value: string) => setTerm(value)}
        onMenuClose={() => setTerm(term)}
        components={{
          Menu: () => null,
          MultiValueLabel: ({ data, innerProps }: OptionProps<OrgOption>) => (
            <div {...innerProps} className="flex items-center gap-1 px-1">
              <span className="helix-body">
                Anyone {data.value.type === 'Team' ? 'On' : 'At'}{' '}
                <span className="helix-body--strong">{data.label}</span>
              </span>
            </div>
          )
        }}
        onFocus={() => showOrgView && setShowOrgView(false)}
        onMenuOpen={() => !showOrgView && setShowMenu(true)}
        onChange={(selected: OrgOption[]) =>
          handleSelectionChange(selected || [])
        }
      />
      {showMenu && (
        <div className="mt-1 rounded-md !border !border-solid !border-gray-100 p-2.5 shadow-md">
          <div>
            <div
              className="helix-mb-2 helix-d-flex helix-align--middle helix-justify--between"
              role="button"
              tabIndex={0}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                }
              }}>
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Suggestions
              </span>
              <div className="helix-switch helix-switch--small">
                <label>
                  <span className="helix-switch__label helix-small text-gray-600">
                    {showOrgView ? 'Close' : 'View'} Organization
                  </span>
                  <input
                    onChange={(e) => {
                      resetSearch()
                      setShowOrgView(e.target.checked)
                    }}
                    type="checkbox"
                    checked={showOrgView}
                  />
                  <div className="helix-switch__control" />
                </label>
              </div>
            </div>
          </div>
          {showMenu &&
            (showOrgView ? (
              <OrganizationHierarchyView
                options={hierarchyOptions}
                selectedOptions={selectedOptions}
                onChange={handleOptionToggle}
              />
            ) : (
              <OrganizationList
                options={filteredListOptions}
                selectedOptions={selectedOptions}
                onChange={handleOptionToggle}
              />
            ))}
        </div>
      )}
    </div>
  )
}
